import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Play, Image as ImageIcon, Star, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ['All', 'Events', 'Team', 'Awards', 'Community', 'Fun'];

// ── Lightbox ──────────────────────────────────────────────────────────────────
const Lightbox = ({ item, onClose }) => {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="lightbox-overlay"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
        data-testid="lightbox-close"
      >
        <X className="w-5 h-5" />
      </button>

      <div
        className="max-w-4xl w-full max-h-[90vh] flex flex-col gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        {item.media_type === 'photo' ? (
          <img
            src={item.media_url}
            alt={item.caption || item.title}
            className="max-h-[70vh] w-full object-contain rounded-xl"
          />
        ) : (
          <video
            controls
            autoPlay
            className="max-h-[70vh] w-full rounded-xl bg-black"
            src={item.media_url}
          />
        )}
        <div className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-4 text-white">
          {item.title && <p className="font-bold text-lg mb-1">{item.title}</p>}
          {item.caption && <p className="text-stone-300 text-sm leading-relaxed">{item.caption}</p>}
          <p className="text-xs text-stone-500 mt-2">{item.category}</p>
        </div>
      </div>
    </div>
  );
};

// ── Media Card ─────────────────────────────────────────────────────────────────
const MediaCard = ({ item, onOpen }) => (
  <div
    data-testid={`activity-card-${item.id}`}
    className="group relative overflow-hidden cursor-pointer bg-stone-100 rounded-xl"
    onClick={() => onOpen(item)}
  >
    {item.media_type === 'photo' ? (
      <div className="aspect-square overflow-hidden">
        <img
          src={item.media_url}
          alt={item.caption || item.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
    ) : (
      <div className="aspect-square relative bg-stone-900 overflow-hidden">
        {item.thumbnail_url ? (
          <img src={item.thumbnail_url} alt={item.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <video
            src={item.media_url}
            className="w-full h-full object-cover"
            preload="metadata"
            muted
          />
        )}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center">
            <Play className="w-6 h-6 text-white fill-white ml-0.5" />
          </div>
        </div>
      </div>
    )}

    {/* Hover overlay */}
    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
      {item.is_featured && (
        <span className="absolute top-3 right-3 bg-amber-500 text-white text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 font-semibold">
          <Star className="w-2.5 h-2.5 fill-white" /> Featured
        </span>
      )}
      {item.title && <p className="text-white font-bold text-sm leading-tight">{item.title}</p>}
      {item.caption && <p className="text-stone-300 text-xs mt-1 line-clamp-2 leading-snug">{item.caption}</p>}
      <span className={`mt-2 text-[10px] px-2 py-0.5 rounded-full self-start font-semibold
        ${item.media_type === 'photo' ? 'bg-emerald-600/80 text-white' : 'bg-blue-600/80 text-white'}`}>
        {item.media_type === 'photo' ? <span className="flex items-center gap-1"><ImageIcon className="w-2.5 h-2.5" /> Photo</span> : <span className="flex items-center gap-1"><Play className="w-2.5 h-2.5" /> Video</span>}
      </span>
    </div>
  </div>
);

// ── Main Page ──────────────────────────────────────────────────────────────────
const SocialActivities = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'photo' | 'video'
  const [loading, setLoading] = useState(true);
  const [lightboxItem, setLightboxItem] = useState(null);

  useEffect(() => {
    axios.get(`${API}/social-activities`)
      .then(r => setItems(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = items.filter(item => {
    const catMatch = filter === 'All' || item.category === filter;
    const typeMatch = typeFilter === 'all' || item.media_type === typeFilter;
    return catMatch && typeMatch;
  });

  const photos = items.filter(i => i.media_type === 'photo').length;
  const videos = items.filter(i => i.media_type === 'video').length;

  return (
    <div className="pt-20 min-h-screen bg-[#FAFAF8]">
      {/* Hero */}
      <section className="bg-white border-b border-stone-200 py-16 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] text-amber-600 font-semibold mb-3">DBK Group Of Companies</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-playfair font-bold text-stone-900 mb-4">
            Social Activities
          </h1>
          <p className="text-lg text-stone-500 max-w-2xl leading-relaxed">
            A glimpse into our culture — team events, celebrations, community engagement, and the moments that define us.
          </p>

          {/* Counters */}
          <div className="flex items-center gap-6 mt-8">
            <div className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-emerald-700" />
              <span className="text-sm font-semibold text-stone-700">{photos} Photos</span>
            </div>
            <div className="w-px h-4 bg-stone-300" />
            <div className="flex items-center gap-2">
              <Play className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-semibold text-stone-700">{videos} Videos</span>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-16 z-30 bg-white/90 backdrop-blur border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-3 flex flex-wrap items-center gap-3">
          {/* Type filter */}
          <div className="flex items-center gap-1 bg-stone-100 rounded-full p-1">
            {[['all', 'All'], ['photo', 'Photos'], ['video', 'Videos']].map(([v, l]) => (
              <button
                key={v}
                onClick={() => setTypeFilter(v)}
                data-testid={`type-filter-${v}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors
                  ${typeFilter === v ? 'bg-emerald-900 text-white' : 'text-stone-600 hover:text-stone-900'}`}
              >
                {l}
              </button>
            ))}
          </div>

          <div className="w-px h-5 bg-stone-300 hidden sm:block" />

          {/* Category filter */}
          <div className="flex flex-wrap items-center gap-1.5">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                data-testid={`cat-filter-${cat}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors
                  ${filter === cat
                    ? 'bg-amber-600 text-white border-amber-600'
                    : 'border-stone-300 text-stone-600 hover:border-amber-600 hover:text-amber-600 bg-white'}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="aspect-square bg-stone-200 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24">
            <ImageIcon className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <p className="text-xl font-playfair text-stone-500">No activities found</p>
            <p className="text-stone-400 text-sm mt-2">Try changing the filter above</p>
          </div>
        ) : (
          <div className="columns-2 sm:columns-3 lg:columns-4 gap-4 space-y-4">
            {filtered.map(item => (
              <div key={item.id} className="break-inside-avoid mb-4">
                <MediaCard item={item} onOpen={setLightboxItem} />
                {(item.title || item.caption) && (
                  <div className="px-1 pt-2 pb-1">
                    {item.title && <p className="text-xs font-bold text-stone-800 leading-snug">{item.title}</p>}
                    {item.caption && <p className="text-[11px] text-stone-500 leading-snug mt-0.5">{item.caption}</p>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Lightbox */}
      {lightboxItem && <Lightbox item={lightboxItem} onClose={() => setLightboxItem(null)} />}
    </div>
  );
};

export default SocialActivities;
